import {
  RatingByProgramIdQuery,
  RatingByProgramIdQueryVariables,
  RATING_BY_PROGRAM_ID,
} from "@graphql/feedback/ratingByProgramId"
import {
  ProgramBySlugQuery,
  ProgramBySlugQueryVariables,
  PROGRAM_BY_SLUG,
} from "@graphql/program/programBySlug"
import { GET_SUGGESTION_PROGRAM, SuggestionProgramProps } from "@graphql/program/suggestionProgram"
import {
  ProgramType,
  TopRatedProgramInhouseQuery,
  TOP_RATED_PROGRAM_INHOUSE,
} from "@graphql/program/topRatedProgram"
import { capitalize, checkProgramTypeTraining, handleChar } from "lib/helpers"
import { gtmEcommerce, gtmEvent } from "lib/gtm"
import _, { sortBy } from "lodash"
import { useContext, useEffect, useState } from "react"
import useSWR, { mutate } from "swr"

import { useLazyQuery } from "@apollo/client"
import ModalFeedbackClass from "@components/Modal/FeedbackClass"
import Overlay from "@components/Modal/Overlay"
import BookmarkAndShare from "@components/Programs/ProgramDetail/BookmarkAndShare"
import Breadcrumbs from "@components/Programs/ProgramDetail/Breadcrumbs"
import Feedback from "@components/Programs/ProgramDetail/Feedback"
import CardStars from "@components/Programs/ProgramDetail/Ratings"
import Summary from "@components/Programs/ProgramDetail/Summary"
import Syllabus from "@components/Programs/ProgramDetail/Syllabus"
import Trainers from "@components/Programs/ProgramDetail/Trainer"
import CardDetailProgram from "@components/shared-components/Card/CardDetailProgram"
import CardProgram from "@components/shared-components/Card/CardProgram"
import TextAndLabel from "@components/shared-components/TextAndLabel"
import Badge from "@components/shared-components/ui/Badge"
import SkeletonProgramDetail from "@components/Skeleton/SkeletonProgramDetail"
import { RatingFeedbackUser } from "@graphql/feedback/ratingByProgramSlug"
import { initializeApollo } from "lib/apollo"
import fetcher from "lib/fetcher"
import Button from "lib/components/Button"
import Divider from "lib/components/Divider"
import Icon from "lib/components/Icons"
import Modal from "lib/components/Modal"
import Text from "lib/components/Text"
import NotFoundTitle from "lib/components/NotFoundTitle"
import { LanguageContext } from "context/LanguageContext"
import sha256 from "crypto-js/sha256"
import { useAuth } from "lib/auth"
import { useCheckout } from "lib/checkout"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"
import { toast } from "react-toastify"
import url from "url"
import redis from "lib/redis"

function compare( a, b ) {
  if ( a.feedback_user.created_at > b.feedback_user.created_at ){
    return -1
  }

  if ( a.feedback_user.created_at < b.feedback_user.created_at ){
    return 1
  }

  return 0
}

const LIMIT_FIXED_PROGRAM_DETAIL = 100
const apolloClient = initializeApollo()
declare const window: {
  dispatchEvent(storageEvent: Event): unknown
  QismoApp
  location
  addEventListener
  scrollY
}
function DetailProgram({ program_detail, totalRating, averageRating }) {
  const { useTranslation: t } = useContext(LanguageContext)
  return (
    <div className="mt-4 lg:mt-10">
      <div>
        <Text weight="semibold" color="default" size="ultra-large" customClass="leading-8">
          {program_detail?.program?.name}
        </Text>
      </div>
      <CardStars averageRating={averageRating} totalRating={totalRating} />
      <div className="mt-2 flex space-x-4">
        <Badge label={program_detail?.program.type} customClass="bg-indigo-100 text-indigo-600" />
        <Badge
          label={program_detail?.program.category.category_name}
          customClass="bg-blue-100 text-blue-600"
        />
      </div>
      <div className="flex gap-4 sm:gap-14 justify-between sm:justify-start mt-6">
        <TextAndLabel
          label={t("level")}
          detail={t(
            `${handleChar({ value: program_detail?.level, replacement: "_" }).toLowerCase()}`,
          )}
        />
        <TextAndLabel
          label={t("duration")}
          detail={
            program_detail &&
            `${program_detail?.training_duration} ${
              program_detail?.training_duration > 1 ? t("days") : t("day")
            }`
          }
        />
        <TextAndLabel
          label={t("session_per_day")}
          detail={`${program_detail?.training_times} ${t("hours")}`}
        />
      </div>
    </div>
  )
}

function LoginOrRegister({ path }) {
  const { useTranslation: t } = useContext(LanguageContext)
  return (
    <div className="mt-2 w-full rounded-lg border px-8 py-6">
      <Text size="small" color="dark" weight="semibold">
        {t("please_login_to_access")}
      </Text>
      <Text size="extra-small" color="gray" weight="medium" customClass="mt-1">
        {t("enjoy_more_benefit_after_you_login")}
      </Text>
      <Button
        customClass="mt-4 w-full h-12"
        rounded="large"
        variant="contained"
        size="small"
        onClick={() => {
          window.location.href = `/login?r=${encodeURIComponent(path)}`
        }}
        color="primary">
        <Text size="extra-small" weight="semibold">
          {t("login_or_sign_up")}
        </Text>
        <div className="flex justify-center">
          <Icon iconName="arrow-right-union" />
        </div>
      </Button>
    </div>
  )
}

function YouMustLoginToAccessIt({ path, isLoggedIn }) {
  const { useTranslation: t } = useContext(LanguageContext)

  return (
    <div>
      {!isLoggedIn && (
        <div className="mt-8">
          <Text size="large" weight="semibold">
            {t("trainer_and_syllabus")}
          </Text>
          <LoginOrRegister path={path} />
        </div>
      )}
    </div>
  )
}

export const getServerSideProps = async (ctx) => {
  const programSlug = ctx.params.program
  const categorySlug = ctx.params.category

  const cacheKey = `program:${categorySlug}:${programSlug}`
  const cacheData = await redis.get(cacheKey)

  if (cacheData) {
    const cachedData = JSON.parse(cacheData)
    return {
      props: cachedData,
    }
  }

  const { data } = await apolloClient.query<ProgramBySlugQuery, ProgramBySlugQueryVariables>({
    variables: {
      slug: programSlug,
      category: categorySlug,
    },
    query: PROGRAM_BY_SLUG,
    fetchPolicy: "cache-first",
  })
  const { data: suggestionProgram } = await apolloClient.query<SuggestionProgramProps>({
    variables: {
      slug: programSlug,
      category: categorySlug,
    },
    query: GET_SUGGESTION_PROGRAM,
    fetchPolicy: "cache-first",
  })
  const { data: dataRating } =
    data?.program_program?.length > 0
      ? await apolloClient.query<RatingByProgramIdQuery, RatingByProgramIdQueryVariables>({
          variables: {
            programId: data?.program_program?.[0]?.id,
          },

          query: RATING_BY_PROGRAM_ID,
          fetchPolicy: "cache-first",
        })
      : {
          data: {
            feedback_feedback_rating: [],
            feedback_feedback_rating_aggregate: {
              aggregate: {
                count: 0,
                avg: {
                  value: 0,
                },
              },
            },
          },
        }

  await redis.set(cacheKey, JSON.stringify({
    data,
    dataRating: {
      feedbackUsers: dataRating.feedback_feedback_rating,
      totalRating: dataRating.feedback_feedback_rating_aggregate.aggregate.count,
      averageRating: dataRating.feedback_feedback_rating_aggregate.aggregate.avg.value,
    },
    programSlug,
    suggestionProgram,
  }), "EX", 60 * 60 * 24 * 7)

  return {
    props: {
      data,
      dataRating: {
        feedbackUsers: dataRating.feedback_feedback_rating,
        totalRating: dataRating.feedback_feedback_rating_aggregate.aggregate.count,
        averageRating: dataRating.feedback_feedback_rating_aggregate.aggregate.avg.value,
      },
      programSlug,
      suggestionProgram,
    },
  }
}

function NextSeoComponent({ data, router }) {
  const { pathname } = url.parse(router.asPath)
  const metaTitle: string = `${data[0].program_detail.program.meta_title}`
  const metaDescription: string = data[0].program_detail.program.meta_description
  const programName: string = `${data[0].program_detail.program.name}`
  const programSummary: string = data[0].program_detail.program.summary
  const programThumbnailUrl: string = data[0].program_thumbnail_url

  return (
    <NextSeo
      title={`${data[0].program_detail.program.meta_title ? metaTitle : programName} | PasarTrainer`}
      description={data[0].program_detail.program.meta_description ? metaDescription : programSummary}
      canonical={`${process.env.NEXT_PUBLIC_URL}${pathname}`}
      openGraph={{
        type: "website",
        locale: "en_ID",
        url: `${process.env.NEXT_PUBLIC_URL}${pathname}`,
        title: `${data[0].program_detail.program.meta_title ? metaTitle : programName} | PasarTrainer`,
        site_name: `${data[0].program_detail.program.meta_title ? metaTitle : programName} | PasarTrainer`,
        description: metaDescription || programSummary,
        images: [
          {
            url: programThumbnailUrl || "/assets/images/program-image.webp",
            alt: `${data[0].program_detail.program.meta_title ? metaTitle : programName} - PasarTrainer`,
            width: 800,
            height: 600,
          },
        ],
      }}
    />
  )
}

const valueRating = (value) => {
  if (value > 1 && value <= 1.5) {
    return 1
  }
  if (value > 2 && value <= 2.5) {
    return 2
  }
  if (value > 3 && value <= 3.5) {
    return 3
  }
  if (value > 4 && value <= 4.9) {
    return 4
  }

  return Math.ceil(value)
}

function DetailInHouse(props) {
  const router = useRouter()
  const { useTranslation: t } = useContext(LanguageContext)
  const { data, dataRating, programSlug, suggestionProgram } = props
  const program_detail = data?.program_program[0]?.program_detail
  const programs: {
    program_thumbnail_url: string
  } = {
    program_thumbnail_url: data?.program_program[0]?.program_thumbnail_url || "",
  }

  const [loadingBookmark, setLoadingBookmark] = useState(false)
  const [bookmarkUrl, setBookmarkUrl] = useState("")
  const { data: dataBookmark } = useSWR(bookmarkUrl, fetcher)
  const { isLoggedIn, userProfile, handleUserVerify } = useAuth()
  const { removeCheckoutData } = useCheckout()
  const { category: categorySlug } = useRouter().query
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [suggestionProgramData, setSuggestionProgramData] = useState<Array<ProgramType>>([])
  const [showVerifyModal, setShowVerifyModal] = useState<boolean>(false)
  const [loadingVerify, setLoadingVerify] = useState<boolean>(false)
  const [isModalFeedback, setModalFeedback] = useState<boolean>(false)
  const [dataFeedback, setDataFeedback] = useState<Array<RatingFeedbackUser>>()
  const [searchFeedback, setSearchFeedback] = useState<string>("")
  const [showChatConfirmation, setShowChatConfirmation] = useState<boolean>(false)
  const [isChatWithUs, setIsChatWithUs] = useState<boolean>(false)
  const [isShowSkeleton, setIsShowSkeleton] = useState<boolean>(true)
  const [populateRating, setPopulateRating] = useState<
    Array<{
      rating: number
      value: number
    }>
  >()
  const [getTopRatedQuery, { data: topRatedProgram }] = useLazyQuery<TopRatedProgramInhouseQuery>(
    TOP_RATED_PROGRAM_INHOUSE,
  )

  useEffect(() => {
    if (userProfile?.user_id) {
      setBookmarkUrl(
        `/api/bookmark/program?programSlug=${programSlug}&userId=${userProfile?.user_id}`,
      )
    }
    setIsShowSkeleton(false)
  }, [userProfile])

  useEffect(() => {
    setIsBookmarked(dataBookmark?.isBookmarked)
  }, [dataBookmark])

  useEffect(() => {
    const initializedRatingProgams = _.reduce(
      dataRating?.feedbackUsers,
      (result, { feedback_user }) => {
        const value = valueRating(feedback_user.ratings_aggregate.aggregate.avg.value)
        result[value] = (result[value] || 0) + 1
        return result
      },
      {},
    )

    const populateRatingPrograms = _.map(initializedRatingProgams, (value, key) => ({
      rating: valueRating(key),
      value,
    }))

    const sortPopulateRating = sortBy(populateRatingPrograms, ({ rating }) => rating)

    setPopulateRating(sortPopulateRating)
    dataRating.feedbackUsers.sort(compare)
    setDataFeedback(dataRating.feedbackUsers)
  }, [])

  useEffect(() => {
    if (program_detail) {
      let sectionName = "All Category"
      const selectedIndexItem = localStorage.getItem("selectedIndexItem") || "1"
      const minPriceData = program_detail.prices.reduce((prev, current) =>
        prev.discount_price < current.discount_price ? prev : current,
      )
      const methodValue = []
      if (program_detail.offline_delivery) {
        methodValue.push("Offline")
      }
      if (program_detail.online_delivery) {
        methodValue.push("Online")
      }

      if (localStorage && localStorage.getItem("sectionName")) {
        sectionName = localStorage.getItem("sectionName")
      }

      const trainerData = program_detail.trainers.map((item) =>
        sha256(`${item.trainer.first_name} ${item.trainer.last_name}`).toString(),
      )

      gtmEvent("ce_program_detail", {
        program_id: data.program_program[0].id,
        program_name: program_detail.program.name,
        program_category: program_detail.program.category.category_name,
        program_class_type: "In House Program",
        method: methodValue.join(", "),
        program_level: program_detail.level,
        program_training_duration:
          program_detail.training_duration > 1
            ? `${program_detail.training_duration} ${checkProgramTypeTraining(
                program_detail.training_duration,
                program_detail.program.type,
              )}`
            : `${program_detail.training_duration} ${checkProgramTypeTraining(
                program_detail.training_duration,
                program_detail.program.type,
              )}`,
        program_training_times:
          program_detail.training_times > 1
            ? `${program_detail.training_times} ${checkProgramTypeTraining(
                program_detail.training_times,
                program_detail.program.type,
                "times",
              )}`
            : `${program_detail.training_times} ${checkProgramTypeTraining(
                program_detail.training_times,
                program_detail.program.type,
                "times",
              )}`,
        program_start_date: null,
        program_end_date: null,
        trainer_name: trainerData.join(", "),
        program_price: minPriceData?.price,
        program_discount: Math.round(minPriceData?.price * (minPriceData?.discount / 100)),
        program_final_price: minPriceData?.discount_price,
        program_training_provider: data.program_program[0].provider_detail.name,
      })

      gtmEcommerce({ ecommerce: null })
      gtmEcommerce({
        event: "view_item",
        ecommerce: {
          value: minPriceData?.discount_price,
          items: [
            {
              item_list_id: sectionName.toLowerCase().replace(/ /g, "-"), // adjust to section id
              item_list_name: sectionName, // adjust to section name
              item_id: data.program_program[0].id, // adjust to item id
              item_name: program_detail.program.name, // adjust to item name
              item_brand: data.program_program[0].provider_detail.name, // adjust to item brand
              item_category: program_detail.program.category.category_name, // adjust to item category
              item_variant: `${capitalize(program_detail.program.type)} Program`, // or Public Program, Bootcamp Program
              index: parseInt(selectedIndexItem, 10), // adjust to the promotion index/slot
              currency: "IDR",
              price: minPriceData?.price,
              discount: Math.round(minPriceData?.price * (minPriceData.discount / 100)),
              quantity: 1,
            },
          ],
        },
      })
    }
  }, [])

  const dataLayerPushProgram = (eventName, shareMethod = null) => {
    const minPriceData = program_detail.prices.reduce((prev, current) =>
      prev.discount_price < current.discount_price ? prev : current,
    )
    const methodValue = []
    if (program_detail.offline_delivery) {
      methodValue.push("Offline")
    }
    if (program_detail.online_delivery) {
      methodValue.push("Online")
    }
    const trainerData = program_detail.trainers.map((item) =>
      sha256(`${item.trainer.first_name} ${item.trainer.last_name}`).toString(),
    )

    gtmEvent(eventName, {
      program_id: data.program_program[0].id,
      program_name: program_detail.program.name,
      program_category: program_detail.program.category.category_name,
      program_class_type: "In House Program",
      method: methodValue.join(", "),
      program_level: program_detail.level,
      program_training_duration:
        program_detail.training_duration > 1
          ? `${program_detail.training_duration} ${checkProgramTypeTraining(
              program_detail.training_duration,
              program_detail.program.type,
            )}`
          : `${program_detail.training_duration} ${checkProgramTypeTraining(
              program_detail.training_duration,
              program_detail.program.type,
            )}`,
      program_training_times:
        program_detail.training_times > 1
          ? `${program_detail.training_times} ${checkProgramTypeTraining(
              program_detail.training_times,
              program_detail.program.type,
              "times",
            )}`
          : `${program_detail.training_times} ${checkProgramTypeTraining(
              program_detail.training_times,
              program_detail.program.type,
              "times",
            )}`,

      program_start_date: null,
      program_end_date: null,
      trainer_name: trainerData.join(", "),
      program_price: minPriceData?.price,
      program_discount: Math.round(minPriceData?.price * (minPriceData?.discount / 100)),
      program_final_price: minPriceData?.discount_price,
      program_training_provider: data.program_program[0].provider_detail.name,
      ...shareMethod,
    })
  }

  const handleSaveToBookmark = async () => {
    setLoadingBookmark(true)
    const type = data && (program_detail?.program?.type as "inhouse" | "public" | "bootcamp")
    try {
      const res = await fetcher("/api/bookmark/add", "POST", {
        program_id: program_detail?.program_id,
        program_type: type,
        user_id: userProfile?.user_id,
        class_id: null,
      })
      if (res) {
        await mutate(`/api/bookmark/program?programSlug=${programSlug}`)
        setIsBookmarked(true)
        toast.success(t("successfully_added_to_bookmark"))
        const minPriceData = program_detail.prices.reduce((prev, current) =>
          prev.discount_price < current.discount_price ? prev : current,
        )
        const methodValue = []
        if (program_detail.offline_delivery) {
          methodValue.push("Offline")
        }
        if (program_detail.online_delivery) {
          methodValue.push("Online")
        }

        const trainerData = program_detail.trainers.map((item) =>
          sha256(`${item.trainer.first_name} ${item.trainer.last_name}`).toString(),
        )
        gtmEvent("ce_program_bookmark", {
          program_id: data.program_program[0].id,
          program_name: program_detail.program.name,
          program_category: program_detail.program.category.category_name,
          program_class_type: "In House Program",
          method: methodValue.join(", "),
          program_level: program_detail.level,
          program_training_duration:
            program_detail.training_duration > 1
              ? `${program_detail.training_duration} ${checkProgramTypeTraining(
                  program_detail.training_duration,
                  program_detail.program.type,
                )}`
              : `${program_detail.training_duration} ${checkProgramTypeTraining(
                  program_detail.training_duration,
                  program_detail.program.type,
                )}`,
          program_training_times:
            program_detail.training_times > 1
              ? `${program_detail.training_times} ${checkProgramTypeTraining(
                  program_detail.training_times,
                  program_detail.program.type,
                  "times",
                )}`
              : `${program_detail.training_times} ${checkProgramTypeTraining(
                  program_detail.training_times,
                  program_detail.program.type,
                  "times",
                )}`,

          program_start_date: null,
          program_end_date: null,
          trainer_name: trainerData.join(", "),
          program_price: minPriceData?.price,
          program_discount: Math.round(minPriceData?.price * (minPriceData?.discount / 100)),
          program_final_price: minPriceData?.discount_price,
          program_training_provider: data.program_program[0].provider_detail.name,
        })
        setIsBookmarked(true)
      }
    } catch (err) {
      toast.error("Oops: Failed add to bookmark")
      setIsBookmarked(false)
    } finally {
      setLoadingBookmark(false)
    }
  }

  const handleRemoveFromBookmark = async () => {
    setLoadingBookmark(true)
    const updatedIdBookmark = await fetcher(
      `/api/bookmark/program?programSlug=${programSlug}&userId=${userProfile?.user_id}`,
      "GET",
    )
    try {
      const res = await fetcher("/api/bookmark/delete", "POST", {
        id: updatedIdBookmark?.id,
      })

      if (res) {
        await mutate(`/api/bookmark/program?programSlug=${programSlug}`)
        toast.success(t("successfully_remove_from_bookmark"))
        setIsBookmarked(false)
      }
    } catch (err) {
      toast.error("Oops: Failed to remove from bookmark")
      setIsBookmarked(true)
    } finally {
      setLoadingBookmark(false)
    }
  }

  useEffect(() => {
    if (suggestionProgram?.program_suggestions?.length < 5) {
      const programHasShow = suggestionProgram.program_suggestions.map(
        (item) => item?.related_program?.id,
      )
      programHasShow.push(data?.program_program[0]?.id)

      getTopRatedQuery({
        variables: {
          limit: 5 - suggestionProgram.program_suggestions.length,
          program_has_show: programHasShow,
        },
      })
    }
  }, [])

  useEffect(() => {
    if (isModalFeedback) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "scroll"
    }
  }, [isModalFeedback])

  useEffect(() => {
    const programData = suggestionProgram.program_suggestions.map((suggestProgram) => {
      const dataRatingSuggest = suggestProgram?.related_program?.feedback?.reduce(
        (accumulator, object) => accumulator + object.ratings_aggregate?.aggregate?.avg?.value,
        0,
      )

      return {
        category_slug: suggestProgram?.related_program?.category?.slug,
        discount: suggestProgram?.related_program?.program_detail?.prices[0].discount,
        discount_price: suggestProgram?.related_program?.program_detail?.prices[0].discount_price,
        final_rating:
          Math.round((dataRatingSuggest / suggestProgram?.related_program?.feedback?.length) * 10) /
          10,
        final_rating_participants:
          suggestProgram?.related_program?.total_feedback?.aggregate?.count,
        price: suggestProgram?.related_program?.program_detail?.prices[0].price,
        program_name: suggestProgram?.related_program?.name,
        program_thumbnail_url: suggestProgram?.related_program?.program_thumbnail_url,
        id: suggestProgram?.related_program?.id,
        slug: suggestProgram?.related_program?.slug,
        tp_name: suggestProgram?.related_program?.provider?.name,
        tp_profile_url: suggestProgram?.related_program?.provider?.profile_url,
        type: suggestProgram?.related_program?.type,
      }
    })
    if (topRatedProgram && topRatedProgram.programs) {
      const mergeData = programData.concat(topRatedProgram.programs)
      if (mergeData.length > 0) {
        gtmEcommerce({ ecommerce: null })
        gtmEcommerce({
          event: "view_item_list",
          ecommerce: {
            items: mergeData.map((item, index) => ({
              item_list_id: "related-program", // adjust to section id
              item_list_name: "Related Program", // adjust to section name
              item_id: item.id, // adjust to item id
              item_name: item.program_name, // adjust to item name
              item_brand: item.tp_name, // adjust to item brand
              item_category: capitalize(item.category_slug, "-"), // adjust to item category
              item_variant: `${capitalize(item.type)} Program`, // or Public Program, Bootcamp Program
              index: index + 1, // adjust to the promotion index/slot
              currency: "IDR",
              price: item.price,
              discount: Math.round(item.price * (item.discount / 100)),
              quantity: 1,
            })),
          },
        })
      }
      return setSuggestionProgramData(mergeData)
    }
    if (programData.length > 0) {
      gtmEcommerce({ ecommerce: null })
      gtmEcommerce({
        event: "view_item_list",
        ecommerce: {
          items: programData.map((item, index) => ({
            item_list_id: "related-program", // adjust to section id
            item_list_name: "Related Program", // adjust to section name
            item_id: item.id, // adjust to item id
            item_name: item.program_name, // adjust to item name
            item_brand: item.tp_name, // adjust to item brand
            item_category: capitalize(item.category_slug, "-"), // adjust to item category
            item_variant: `${capitalize(item.type)} Program`, // or Public Program, Bootcamp Program
            index: index + 1, // adjust to the promotion index/slot
            currency: "IDR",
            price: item.price,
            discount: Math.round(item.price * (item.discount / 100)),
            quantity: 1,
          })),
        },
      })
    }
    return setSuggestionProgramData(programData)
  }, [topRatedProgram])

  const openChat = (fullURL, programName) => {
    const qiscusApp = window.QismoApp
    const translateInterestClass = t("interested_in_class")

    const textMessage = `Hi, ${translateInterestClass} "${programName}" ${fullURL}`
    const element: HTMLElement = document.getElementsByClassName(
      "qcw-trigger-btn",
    )[0] as HTMLElement

    element.click()
    if (qiscusApp) {
      qiscusApp.sendMessage({
        event_name: "send-message",
        text: textMessage,
      })
    } else {
      toast.error(t("cannot_send_message"))
    }
  }

  const handleOpenChat = () =>
    openChat(`${process.env.NEXT_PUBLIC_URL + router.asPath}`, program_detail?.program?.name)

  const handleToggleVerifyModal = () => {
    setShowVerifyModal(!showVerifyModal)
  }

  const handleCheckout = () => {
    setIsChatWithUs(false)
    removeCheckoutData()

    if (!isLoggedIn) {
      return router.push(`/login?r=${encodeURIComponent(router.asPath)}`)
    }

    if (!userProfile?.is_verified) {
      return handleToggleVerifyModal()
    }

    dataLayerPushProgram("ce_chat_with_us", null)
    localStorage.removeItem("qiscusApp")
    const storageEvent = new Event("storage")
    window.dispatchEvent(storageEvent)
    return handleOpenChat()
  }

  const handleVerifyAccount = async () => {
    setLoadingVerify(true)
    try {
      await handleUserVerify()
    } catch (err) {
      setLoadingVerify(false)
    }
  }

  if (data?.program_program?.length === 0) {
    return (
      <div className="mt-2 flex justify-center">
        <NotFoundTitle title={t("program_not_found")} />
      </div>
    )
  }

  return (
    <section className="overflow-hidden">
      <style>{`
        @media only screen and (max-width: 37.5rem) {
          .qcw-trigger-btn {
            margin-bottom: 1rem !important;
          }
          #qiscus-app-button-close {
            bottom: 160px;
          }
          .footer {
            margin-bottom: 4.3rem !important;
          }
        }
      `}</style>
      <NextSeoComponent data={data?.program_program} router={router} />
      {isShowSkeleton ? (
        <div className="px-4 lg:px-0">
          <SkeletonProgramDetail />
        </div>
      ) : (
        <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="order-2 col-span-1 w-full lg:order-1 lg:p-0">
                <div className="mt-4 hidden lg:block">
                  <Breadcrumbs
                    category={{
                      name: program_detail.program.category.category_name,
                      slug: categorySlug as string,
                    }}
                    programDetail={data.program_program[0].program_detail}
                  />
                </div>
                <DetailProgram
                  averageRating={dataRating?.averageRating}
                  totalRating={dataRating?.totalRating}
                  program_detail={program_detail}
                />
                {/* button bookmark */}
                <BookmarkAndShare
                  onSaveBookmark={handleSaveToBookmark}
                  onUnSaveBookmark={handleRemoveFromBookmark}
                  loadingBookmark={loadingBookmark}
                  isBookmarked={isBookmarked}
                  isLoggedIn={isLoggedIn}
                  dataLayerPushProgram={dataLayerPushProgram}
                />
                {/* button bookmark */}

                {/* divider */}
                <Divider customClass=" border-gray-100 mt-8 border-t-0" />
                {/* divider */}

                {/* summary */}
                <Summary program_detail={program_detail} />
                {/* summary */}

                {/* trainer section */}
                {isLoggedIn && (
                  <Trainers
                    program_detail={program_detail}
                    slugProvider={data?.program_program?.[0]?.provider_detail.slug}
                  />
                )}

                {/* trainer section */}

                {/* Feedback */}
                {dataRating.totalRating > 0 && (
                  <Feedback
                    feedbackUser={dataRating.feedbackUsers}
                    averageRating={dataRating.averageRating}
                    totalRating={dataRating.totalRating}
                    onClick={() => setModalFeedback(!isModalFeedback)}
                  />
                )}

                {/* Feedback */}

                {/* You must login before access syllabus or trainer */}
                <YouMustLoginToAccessIt path={router.asPath} isLoggedIn={isLoggedIn} />
                {/* You must login before access syllabus or trainer */}

                {/* sillabus */}
                {isLoggedIn && <Syllabus program_detail={program_detail} type="inhouse" />}
                {/* sillabus */}
              </div>
              {/* detail card program */}
              <div className="order-1 grid-cols-6 lg:order-2 lg:grid">
                <div className="col-span-3" />
                <div className="relative col-span-3 mt-4 w-full lg:mt-20 lg:w-full">
                  <div className="mb-6 block lg:hidden">
                    <Breadcrumbs
                      category={{
                        name: program_detail.program.category.category_name,
                        slug: categorySlug as string,
                      }}
                      programDetail={data.program_program[0].program_detail}
                    />
                  </div>

                  <div>
                    <div className="w-full top-0 left-0 z-40">
                      {program_detail && (
                        <CardDetailProgram
                          rating={dataRating?.totalRating || 0}
                          ratingParticipants={program_detail && dataRating?.averageRating}
                          prices={program_detail && program_detail.prices}
                          offlineDelivery={program_detail && program_detail.offline_delivery}
                          onlineDelivery={program_detail && program_detail.online_delivery}
                          classDetail={program_detail}
                          id={program_detail?.program?.id}
                          isActive={data?.program_program?.[0].is_publish && !data?.program_program?.[0].is_restricted}
                          program={programs}
                          handleVerifyAccount={handleVerifyAccount}
                          handleCheckout={() => {
                            setShowChatConfirmation(!showChatConfirmation)
                          }}
                          handleToggleVerifyModal={handleToggleVerifyModal}
                          loadingVerify={loadingVerify}
                          showVerifyModal={showVerifyModal}
                          type="inhouse"
                          customComponent={
                            <BookmarkAndShare
                              customClass="m-3 block lg:hidden"
                              customClassBookmark="block lg:hidden"
                              onlyBookmark
                              onSaveBookmark={handleSaveToBookmark}
                              onUnSaveBookmark={handleRemoveFromBookmark}
                              loadingBookmark={loadingBookmark}
                              isBookmarked={isBookmarked}
                              isLoggedIn={isLoggedIn}
                              dataLayerPushProgram={dataLayerPushProgram}
                            />
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* detail card program */}
            </div>
            {/* related Programs */}
            {suggestionProgramData?.length > 0 ? (
              <div className="py-14">
                <div className="pb-8">
                  <Text size="large" weight="semibold" color="dark">
                    {t("related_program")}
                  </Text>
                </div>
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-5 lg:gap-6">
                  {suggestionProgramData.map((programData, index) => (
                    <CardProgram
                      sectionName="Related Program"
                      index={index}
                      key={index}
                      category={{
                        slug: programData?.category_slug,
                        name: programData?.category_name,
                        description: programData?.category_description,
                      }}
                      discount={programData?.discount}
                      discountPrice={programData?.discount_price}
                      finalRating={programData?.final_rating}
                      finalRatingParticipants={programData?.final_rating_participants}
                      price={programData?.price}
                      programName={programData?.program_name}
                      programThumbnailUrl={programData?.program_thumbnail_url}
                      id={programData?.id}
                      slug={programData?.slug}
                      tpName={programData?.tp_name}
                      tpProfileUrl={programData?.tp_profile_url}
                      type={programData?.type}
                      customImage={programData.custom_image}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="pb-14" />
            )}

            {/* modal chat confirmation */}
            {showChatConfirmation && (
              <Modal
                onClose={() => {
                  setShowChatConfirmation(!showChatConfirmation)
                  setIsChatWithUs(false)
                }}>
                <div className="flex flex-col gap-8 items-center pt-6 pb-3">
                  <div className="hidden lg:block">
                    <Text alignText="center">{t("we_are_connecting_you_with_our_team")}</Text>
                  </div>
                  <div className="block lg:hidden">
                    <Text alignText="center">{`${t("we_are_connecting_you_with_our_team")}`}</Text>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => {
                        setShowChatConfirmation(!showChatConfirmation)
                        setIsChatWithUs(false)
                        document.body.style.overflowY = "scroll"
                      }}>
                      {t("cancel")}
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      loading={isChatWithUs}
                      onClick={() => {
                        setShowChatConfirmation(!showChatConfirmation)
                        setIsChatWithUs(true)
                        handleCheckout()
                        document.body.style.overflowY = "scroll"
                      }}>
                      {t("continue")}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
          </div>
      )}

      <ModalFeedbackClass
        dataFeedback={dataFeedback}
        dataRating={dataRating}
        handleDataFeedback={(value) => setDataFeedback(value)}
        handleModalFeedback={() => setModalFeedback(!isModalFeedback)}
        isModalFeedback={isModalFeedback}
        handleSearchFeedback={(value) => {
          setSearchFeedback(value)
        }}
        populateRating={populateRating}
      />

      {isModalFeedback && <Overlay />}
    </section>
  )
}

export default DetailInHouse
